"use client";

import React, { forwardRef, useState, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import * as cva from "./input.cva";
import { InputForward } from "./input.type";
import { useClassnames } from "@packages/utils/use-classnames";

const Input: InputForward = (props, ref) => {
  const {
    id,
    classNames,
    disabled,
    size,
    leftIcon,
    rightIcon,
    inputPrefix,
    inputSuffix,
    bgColor,
    value,
    defaultValue,
    onChange,
    ...others
  } = props;

  const [inputValue, setInputValue] = useState(defaultValue || "");

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    setInputValue(e.target.value);
  };

  const cx = useClassnames({
    root: cva.rootCva(),
    input: cva.inputCva(),
    leftIcon: cva.leftIconCva(),
    rightIcon: cva.rightIconCva(),
  });

  const sizeClasses = {
    "32px": "h-8",
    "40px": "h-10",
    "48px": "h-12",
    "56px": "h-14",
  };

  return (
    <div className={twMerge("relative w-full", classNames?.root)}>
      <div className={twMerge("relative", classNames?.container)}>
        {leftIcon && (
          <div className={twMerge(cx.leftIcon, classNames?.leftIcon)}>
            {leftIcon}
          </div>
        )}
        {inputPrefix && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 pl-3">
            {inputPrefix}
          </div>
        )}
        <input
          id={id}
          ref={ref}
          disabled={disabled}
          className={twMerge(
            cx.input,
            leftIcon && "pl-10",
            inputPrefix && "pl-8",
            inputSuffix && "pr-8",
            rightIcon && "pr-10",
            classNames?.input,
            bgColor,
            sizeClasses[size as keyof typeof sizeClasses],
          )}
          value={inputValue}
          onChange={handleChange}
          {...others}
        />
        {inputSuffix && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-3">
            {inputSuffix}
          </div>
        )}
        {rightIcon && (
          <div className={twMerge(cx.rightIcon, classNames?.rightIcon)}>
            {rightIcon}
          </div>
        )}
      </div>
    </div>
  );
};

Input.displayName = "Input";

export default forwardRef(Input);
